import { useTranslation } from "react-i18next";
import { useCompanyMembers } from "@/hooks/useCompanyMembers";
import { displayNameFor } from "@/lib/avatarColor";
import { movementKind, type MovementRow } from "@/lib/partTakeOut";
import { Skeleton } from "@/components/ui/skeleton";

interface Props {
  movements: MovementRow[];
  binAddressFor: (binId: string | null) => string | null;
  isLoading?: boolean;
}

function formatWhen(iso: string) {
  const d = new Date(iso);
  return `${d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })} · ${d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false })}`;
}

export function PartMovementsCard({ movements, binAddressFor, isLoading = false }: Props) {
  const { t } = useTranslation("inventory");
  const { data: members = [] } = useCompanyMembers();
  const nameOf = (id: string) => {
    const m = members.find((x) => x.id === id);
    return m ? displayNameFor(m.name, m.email) : id.slice(0, 8);
  };

  return (
    <div className="bg-card rounded-xl border border-border p-6">
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium mb-4">
        {t("movements.title")}
      </div>
      {isLoading ? (
        <Skeleton className="h-5 w-40" />
      ) : movements.length === 0 ? (
        <div className="text-sm text-muted-foreground">{t("movements.empty")}</div>
      ) : (
        <ul className="divide-y divide-border">
          {movements.map((m) => {
            const kind = movementKind(m);
            const label = kind === "takenOut"
              ? t("movements.takenOut", { reason: t(`takeOut.reasons.${m.reason}`) })
              : t(`movements.${kind}`);
            const addr = binAddressFor(m.bin_id);
            return (
              <li key={m.id} className="py-2.5 text-sm">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="font-medium text-foreground">
                    {label}
                    {addr && <span className="ml-2 font-mono text-xs text-muted-foreground">{addr}</span>}
                  </span>
                  <span className="text-xs text-muted-foreground">{formatWhen(m.performed_at)}</span>
                </div>
                <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
                  <span>{nameOf(m.performed_by)}</span>
                  {m.backfilled && (
                    <span className="px-1.5 py-0.5 rounded bg-muted text-[10px] uppercase tracking-wide">{t("movements.backfilled")}</span>
                  )}
                </div>
                {m.note && kind !== "putBack" && <div className="text-xs text-foreground mt-1">{m.note}</div>}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
